<template>
  <div class="column full-width no-wrap">
    <AttendanceChatInputReplyPreview
      v-if="replyingTo"
      :message="replyingTo"
      :contact-name="contactName"
      @cancel-reply="$emit('cancel-reply')"
    />
    <div class="col-auto">
      <UploadContainer
        :ref-id="refId"
        :ref-type="refType"
      />
    </div>

    <CommandsList
      :open="open"

      :options="suggestions"
      v-model="indexSuggestion"

      @on-close="onCloseSuggestion"
      @on-click="onSelectSuggestion"
    />

    <div class="col-auto row items-center full-height content-center input-target-chat q-pt-sm">
      <div class="col-auto">
        <UploadButton
          v-if="!inputDisable"
          :ref-id="refId"
          :ref-type="refType"
        />
      </div>

      <div
        class="col full-width justify-end"
        style="position: relative"
      >
        <textarea
          ref="message"
          data-testid="chat-mensagem"
          rows="1"
          class="col full-width overflow-auto input-message"
          style="resize: none;"
          placeholder="Digite sua mensagem"
          v-model="message"
          :disabled="inputDisable"
          maxlength="3836"
          @keydown.enter.exact.prevent="handleMessage"
          @keydown.esc.prevent="onCloseSuggestion"
          @keydown.up.exact="updateActiveUpOrDown(-1)"
          @keydown.down.exact="updateActiveUpOrDown(1)"
          @keydown.shift.enter.exact="autoResize"
          @keydown.delete="autoResize"
          @input="handleInput"
          @paste="autoResize"
        />

        <q-btn-dropdown
          data-testid="chat-emojis"
          dense
          flat
          no-icon-animation
          padding="0"
          class="col-auto q-mx-xs input-icons bg-white"
          :disable-dropdown="!canSendMessage || inputDisable "
        >
          <template #label>
            <EmoisIcon />
          </template>

          <FieldEmojis
            v-model="message"
            style="min-width: 30em; max-height: 17em;"
          />
        </q-btn-dropdown>
      </div>

      <div class="col-auto">
        <UploadVoice
          v-if="!inputDisable"
          :ref-id="refId"
          :ref-type="refType"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import UploadVoice from '@/components/UploadVoice'
import FieldEmojis from './FieldEmojis'
import UploadButton from '@/components/UploadButton'
import UploadContainer from '@/components/UploadContainer'
import { isImage, isAudioType, isVideoType, TYPES_MESSAGE } from '@/constants/attachmentTypes'
import CommandsList from './CommandsList.vue'
import AttendanceChatInputReplyPreview from './AttendanceChatInputReplyPreview.vue'
import EmoisIcon from '@/assets/icons/EmoisIcon.svg'

export default {
  components: {
    UploadVoice,
    UploadButton,
    UploadContainer,
    CommandsList,
    AttendanceChatInputReplyPreview,
    EmoisIcon,
    FieldEmojis
  },

  emits: [
    'on-lines',
    'cancel-reply',
    'on-message',
    'on-command'
  ],

  props: {
    refId: {},
    refType: {},
    number: {},
    inputDisable: {
      type: Boolean,
      default: false
    },
    contactName: {
      type: String,
      default: ''
    },
    replyingTo: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      open: false,
      maxInput: '3836',
      message: this.getMessageState(),
      invalidChar: 'Você precisa digitar um código válido de Agente!',
      debounceTimeout: null,

      indexSuggestion: 0,
      rootSuggestions: { options: [], selected: -1 }
    }
  },

  mounted () {
    this.$store.dispatch('agentModule/reloadSession')
    this.autoResize()
  },

  beforeDestroy () {
    const vm = this
    if (vm.debounceTimeout) clearTimeout(vm.debounceTimeout)
  },

  computed: {
    ...mapState({
      attachments ({ uploadModule }) {
        const files = Array.isArray(uploadModule.files) ? [...uploadModule.files] : []
        return files.filter((attach) => {
          return attach.refId === this.refId && attach.refType === this.refType
        })
      }
    }),

    ...mapGetters({
      isExceeded: 'uploadModule/isExceeded'
    }),

    inputCount () {
      return this.message.length
    },

    canSendMessage () {
      return this.inputCount <= this.maxInput
    },

    suggestions () {
      const suggestions = getNextSuggestions(this.rootSuggestions).options
      return suggestions.filter(byLabel(this.message))
    },

    isCommand () {
      const vm = this
      const commands = vm.suggestions.filter((item) => item.label[0] === '/' && !item.options?.length).map((item) => toLower(item.label))
      return Boolean(commands.find((command) => toLower(vm.message).startsWith(command)))
    }
  },

  methods: {
    ...mapActions({
      setInput: 'uploadModule/setInput',
      startUpload: 'uploadModule/startUpload',
      removeAttach: 'uploadModule/removeAttach',
      setIsExceeded: 'uploadModule/setIsExceeded'
    }),

    focus () {
      this.$refs.message.focus()
    },

    autoResize (event) {
      const vm = this
      vm.handlePaste(event)
      if (vm.debounceTimeout) clearTimeout(vm.debounceTimeout)

      vm.debounceTimeout = setTimeout(() => {
        if (vm.adjustTextArea()) return
        clearTimeout(vm.debounceTimeout)
        vm.debounceTimeout = null
      }, 180)
    },

    adjustTextArea () {
      const vm = this
      if (!vm.$refs.message) return false
      const textarea = vm.$refs.message
      const virtualTextarea = document.createElement('textarea')
      const computedStyle = getComputedStyle(textarea)
      virtualTextarea.style.width = computedStyle.width
      virtualTextarea.style.display = 'block'
      virtualTextarea.style.font = computedStyle.font
      virtualTextarea.style.fontSize = computedStyle.fontSize
      virtualTextarea.style.padding = computedStyle.padding
      virtualTextarea.style.border = computedStyle.border
      virtualTextarea.style.lineHeight = computedStyle.lineHeight
      virtualTextarea.style.whiteSpace = computedStyle.whiteSpace
      virtualTextarea.style.overflowY = 'hidden'
      virtualTextarea.style.position = 'absolute'
      virtualTextarea.style.visibility = 'hidden'
      virtualTextarea.value = vm.message
      virtualTextarea.style.height = '31px'
      document.body.appendChild(virtualTextarea)
      let height = virtualTextarea.scrollHeight
      document.body.removeChild(virtualTextarea)
      if (height < 31) height = 31
      if (height > 146) height = 146
      height = `${height}px`

      if (height !== textarea.style.height) {
        textarea.style.height = height
        setTimeout(() => vm.$emit('on-lines'), 550)
      }

      return true
    },

    handleInput () {
      const vm = this
      if (vm.message.length <= vm.maxInput) return vm.autoResize()
      vm.message = vm.message.substring(0, vm.maxInput)
      newNotification('ALERTA', 'Limite de caracteres atingido, a documentação meta não permite entregar mensagem acima de 4096 caracteres')
    },

    handlePaste (event) {
      const items = event?.clipboardData?.items ?? []

      const text = event?.clipboardData?.getData('text')
      if (text) return

      const files = []

      for (const item of items) {
        if (item.kind === 'file') {
          files.push(item.getAsFile())
          event.preventDefault()
        }
      }

      if (!files.length) return

      this.startUpload({
        refId: this.refId,
        refType: this.refType,
        files
      })
    },

    saveMessage () {
      const vm = this

      vm.setInput({
        refId: vm.refId,
        refType: vm.refType,
        message: vm.message
      })
    },

    getMessageState () {
      const vm = this
      const state = vm.$store.state.uploadModule
      if (!state) return ''
      if (!state.input) return ''
      if (!state.input[vm.refType]) return ''
      if (!state.input[vm.refType][vm.refId]) return ''
      return state.input[vm.refType][vm.refId] || ''
    },

    async handleMessage () {
      const vm = this
      if (vm.inputDisable || !vm.canSendMessage) return

      const attachments = Array.isArray(vm.attachments) ? [...vm.attachments] : []
      if (!vm.message && !attachments.length) return

      if (vm.open) {
        vm.onSelectSuggestion(this.indexSuggestion)
        return
      }

      if (vm.isCommand) {
        const destination = Number(vm.message.split(' ').pop())

        if (!destination) return newNotification('ERRO', vm.invalidChar)
        vm.$emit('on-command', { destination })
        vm.message = ''
        return
      }

      const msgCaption = String(vm.message || '').trim()
      const fileCaption = attachments.length === 1 && Boolean(msgCaption) ? msgCaption : ''

      for (const attach of attachments) {
        const attachMessage = mountMessage(attach, fileCaption, vm.replyingTo?.ref)
        if (attachMessage) {
          if (hasCaption({ type: attachMessage.type })) vm.message = ''
          vm.$emit('on-message', attachMessage)
          vm.removeAttach(attach)
          if (attachments.length) await wait(250)
        }
      }

      const textMessage = mountMessage(vm.message, undefined, vm.replyingTo?.ref, attachments.length > 0)
      if (!textMessage) return
      if (attachments.length) await wait(850)
      vm.$emit('on-message', textMessage)
      vm.message = ''
    },

    onCloseSuggestion () {
      if (this.open) this.open = false
    },

    updateActiveUpOrDown (direction) {
      const vm = this
      if (!vm.open) return
      if (!vm.suggestions.length) return

      const maxIndex = vm.suggestions.length - 1
      let indexSuggestion = vm.indexSuggestion + direction
      if (indexSuggestion < 0) indexSuggestion = maxIndex
      if (indexSuggestion > maxIndex) indexSuggestion = 0
      if (indexSuggestion === vm.indexSuggestion) return

      vm.indexSuggestion = indexSuggestion
    },

    setNextSuggestions (selected) {
      const vm = this
      const rootSuggestions = JSON.parse(JSON.stringify(vm.rootSuggestions))
      getNextSuggestions(rootSuggestions).selected = selected

      const message = '/'
      const indexSuggestion = 0
      Object.assign(vm, { message, indexSuggestion, rootSuggestions })
    },

    clearSuggestions () {
      const vm = this
      const indexSuggestion = 0
      const rootSuggestions = clearSuggestions(JSON.parse(JSON.stringify(vm.rootSuggestions)))
      Object.assign(vm, { indexSuggestion, rootSuggestions })
    },

    onSelectSuggestion (indexSuggestion) {
      const vm = this
      if (!vm.open) return
      if (!vm.suggestions.length) return
      if (indexSuggestion < 0) return
      if (indexSuggestion > vm.suggestions.length - 1) return

      const option = vm.suggestions.at(indexSuggestion)
      const hasSubOptions = Boolean(option.options?.length)
      if (hasSubOptions) {
        const currentLevel = getNextSuggestions(vm.rootSuggestions)
        const realIndex = currentLevel.options.findIndex(opt => opt.id === option.id)
        return vm.setNextSuggestions(realIndex)
      }

      const message = String(vm.message).trim()
      const hasParams = message.split(' ').length > 1

      if (hasParams) {
        vm.open = false
        return vm.$nextTick().then(vm.handleMessage)
      }

      vm.message = option.label
    }
  },

  watch: {
    isExceeded: {
      deep: true,
      handler () {
        const state = this.$store.state.uploadModule
        const newLink = state.input[this.refType][this.refId]

        if (!this.message.includes(newLink)) {
          this.message = this.message.concat('\n \n ', newLink)
        }
        this.setIsExceeded(false)
      }
    },

    message: {
      deep: true,
      handler (message, oldMsg) {
        const vm = this
        const msg = toLower(message)
        if (msg === toLower(oldMsg)) return

        if (msg[0] === '/' && !vm.open) {
          const groupQuickAnswers = vm.$store.state.settingsModule.groupQuickAnswers === 'yes'
          const getterQuickAnswers = groupQuickAnswers ? 'groupSuggestions' : 'listSuggestions'
          const quickAnswers = vm.$store.getters[`quickAnswersModule/${getterQuickAnswers}`]
          vm.rootSuggestions.options = defaultSuggestions(quickAnswers)
          vm.open = true
        }

        if (msg[0] !== '/' && vm.open) vm.open = false
        if (!msg) vm.clearSuggestions()
        vm.saveMessage()
        vm.autoResize()
      }
    },

    suggestions: {
      deep: true,
      handler (newSuggestions, oldSuggestions) {
        const vm = this
        if (!vm.open) return
        if (isEqual(newSuggestions, oldSuggestions)) return
        vm.open = false
        vm.$nextTick().then(() => {
          vm.open = true
        })
      }
    }
  }
}

const defaultSuggestions = (otherOptions) => (
  [{
    id: 'transferir',
    label: '/transferir ',
    option: 'matricula',
    class: 'option-command',
    description: 'Transfira para o Código do Agente'
  }, ...otherOptions]
)

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const mountMessage = (content, caption, parentRef) => {
  if ([null, undefined, ''].includes(content)) return null
  const isAttach = typeof content === 'object' && Boolean(content?.id)
  const baseMessage = parentRef ? { parentRef } : {}

  if (isAttach) {
    if (!String(content.fileUrl || '').trim()) return null
    let type = TYPES_MESSAGE.DOC
    if (isImage(content)) type = TYPES_MESSAGE.IMAGE
    if (isAudioType(content)) type = TYPES_MESSAGE.AUDIO
    if (isVideoType(content)) type = TYPES_MESSAGE.VIDEO
    const fileCaption = hasCaption({ type }) && Boolean(String(caption || '').trim()) ? String(caption || '').trim() : undefined
    return { ...baseMessage, type, message: JSON.stringify({ ...content, fileCaption }) }
  }

  if (!String(content || '').trim()) return null
  return { ...baseMessage, message: content.trim(), type: TYPES_MESSAGE.TEXT }
}

const getNextSuggestions = (suggestions) => {
  if (suggestions?.selected === -1) return suggestions
  return getNextSuggestions(suggestions.options[suggestions.selected])
}

const clearSuggestions = (suggestions) => {
  if (suggestions.options) {
    suggestions.selected = -1
    suggestions.options.forEach(clearSuggestions)
  }

  return suggestions
}

const hasCaption = ({ type }) => ([
  TYPES_MESSAGE.DOC,
  TYPES_MESSAGE.VIDEO,
  TYPES_MESSAGE.IMAGE
].includes(type))

const newNotification = (title, body) => (
  new Notification(title, { body })
)

const byLabel = (msg) => {
  const arg = clearArg(msg)

  return (item) => {
    const label = clearArg(item.label)
    if (label.includes(arg)) return true

    for (const word of arg.split(' ')) {
      if (label.includes(word.trim())) return true
    }

    return false
  }
}

const isEqual = (source, target) => (
  JSON.stringify(source) === JSON.stringify(target)
)

const clearArg = (val) => (
  toLower(val[0] === '/' ? val.split('').splice(1, val.length).join('') : val)
)

const toLower = (val) => (
  String(val || '').toLowerCase().trim()
)

</script>

<style lang="sass" scoped>

.count-input.exceeded
  color: red

.send-icon
  width: 21px
  height: 21px
  right: 0
  transform: matrix(0.95, 0.34, -0.31, 0.94, 0, 0)
  filter: invert(22%) sepia(98%) saturate(1291%) hue-rotate(188deg) brightness(65%) contrast(97%)

.input-message
  border: 1px solid #CDDCE5
  padding: 7px
  color: #01253C
  font-weight: normal
  font-size: 13px
  line-height: 15px
  border-radius: 20px
  transition: height 0.5s ease
  &:focus
    outline: none
    border-color: #1769B6
    box-shadow: 0 0 0 2px rgba(23, 105, 182, 0.15)
  &:hover
    border: 1px solid #1769B6
  &::-webkit-input-placeholder
    color: #01253C
    opacity: 0.5
  &::-webkit-scrollbar-track
    background: transparent
</style>
